import {
  CsvExport,
  ExcelExport,
  ExcelSheet,
} from '../models/api/core/export/export';

export class StatusHistoryExportMapper {
  static toCsvExport(rows: any[], columns: any[], fileName: string): CsvExport {
    const uniqueColumns = this.combineColumns(columns);
    const headers = uniqueColumns.map(this.getLabel);
    const dataRows = rows.map((row) =>
      uniqueColumns.map((col) => this.getColumnValue(row, col.name)),
    );

    return {
      fileName,
      headers,
      rows: dataRows,
    };
  }

  static toExcelExport(
    rows: any[],
    columns: any[],
    fileName: string,
    sheetLabel: string,
  ): ExcelExport {
    const uniqueColumns = this.combineColumns(columns);
    const headers = uniqueColumns.map(this.getLabel);
    const dataRows = rows.map((row) =>
      uniqueColumns.map((col) => this.getColumnValue(row, col.name)),
    );

    const sheet: ExcelSheet = {
      label: sheetLabel,
      headers,
      rows: dataRows,
    };

    return {
      fileName,
      sheets: [sheet],
    };
  }

  private static combineColumns(columns: any[]): any[] {
    const seen = new Set<string>();
    const uniqueColumns: any[] = [];

    const headerOrder = [
      'Status Date',
      'Claim Status',
      'Changed By',
      'Reopened',
      'Reason Codes',
    ];

    for (const desiredHeader of headerOrder) {
      for (const column of columns) {
        const label = this.getLabel(column);
        if (label === desiredHeader && !seen.has(label)) {
          seen.add(label);
          uniqueColumns.push(column);
          break;
        }
      }
    }

    return uniqueColumns;
  }

  private static getLabel(column: any): string {
    const headerMappings: Record<string, string> = {
      dateStat: 'Status Date',
      clmStatus: 'Claim Status',
      subStatusDesc: 'Claim Status',
      changedBy: 'Changed By',
      reopenFlag: 'Reopened',
      reasonCodes: 'Reason Codes',
    };

    return headerMappings[column.name] || column.label || column.name;
  }

  private static getColumnValue(row: any, name: string): any {
    if (!row || !name) return undefined;

    const keys = Object.keys(row);
    const lowerKeyMap = keys.reduce(
      (acc, key) => {
        acc[key.toLowerCase()] = key;
        return acc;
      },
      {} as Record<string, string>,
    );

    if (
      name.toLowerCase() === 'datestat' ||
      name.toLowerCase() === 'statusdate'
    ) {
      const dateKey = lowerKeyMap['datestat'];
      return dateKey ? row[dateKey] : undefined;
    }

    if (name.toLowerCase() === 'changedby') {
      const changedByKey = lowerKeyMap['changedby'];
      return changedByKey ? row[changedByKey] : undefined;
    }

    if (
      name.toLowerCase() === 'clmstatus' ||
      name.toLowerCase() === 'status' ||
      name.toLowerCase() === 'substatusdesc' ||
      name.toLowerCase() === 'substatus'
    ) {
      const statusKey = lowerKeyMap['clmstatus'];
      const subStatusKey = lowerKeyMap['substatusdesc'];
      const status = statusKey ? row[statusKey] : '';
      const subStatus = subStatusKey ? row[subStatusKey] : '';

      if (status && subStatus) {
        return `${status} - ${subStatus}`;
      } else if (status) {
        return status;
      } else if (subStatus) {
        return subStatus;
      }
      return undefined;
    }

    if (
      name.toLowerCase() === 'reopenflag' ||
      name.toLowerCase() === 'reopened'
    ) {
      const reopenKey = lowerKeyMap['reopenflag'];
      if (
        reopenKey &&
        row[reopenKey] !== undefined &&
        row[reopenKey] !== null
      ) {
        return row[reopenKey] ? 'Yes' : 'No';
      }
      return undefined;
    }

    if (name.toLowerCase() === 'reasoncodes') {
      const reasonKey = lowerKeyMap['reasoncodes'];
      return reasonKey ? row[reasonKey] : undefined;
    }

    // Default case - try to find the exact key match
    const actualKey = lowerKeyMap[name.toLowerCase()];
    return actualKey ? row[actualKey] : undefined;
  }
}
